import * as React from 'react';

import type {DisplayOptionEntity} from 'nutshell-core/utils';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {ContactCardCreateable} from 'shells/peep-cards/contact-card/contact-card-createable/contact-card-createable';
import type {ContactInput} from 'shells/peep-cards/contact-card/types';

import type {
    ContactCreateInput,
    ContactUpdateInput,
    ContactCreateValidationInput,
    EntityRelatedEntityInput,
    ContactForCoreEntityFragment,
} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';

import {getPotentialContacts} from '../../../../core/form/fields/peeps/contacts/rich-contact-select-helpers';
import {useGetContactFormValidation} from '../../../../core/graphql/hooks';
import type {Option} from '../../../../core/select/graphql-select-creatable';

type Props = {
    leadId: string;
    onClose: () => void;
    onCreate: (arg1: ContactCreateInput) => Promise<any>;
    onUpdate: (arg1: ContactUpdateInput) => Promise<any>;
    contactParentAccount?: Option | null;
    alreadyAddedAccountIds: string[];
    errorProps: {
        error: string | null | undefined;
        setError: (error?: string | null | undefined) => void;
    };
    alreadyAddedContacts: Array<ContactForCoreEntityFragment>;
};

export function ContactRowCreateForm(props: Props) {
    const {refetch: fetchFormValidation} = useGetContactFormValidation();

    const handleRefetchTrigger = React.useCallback(() => {
        if (NutClientConfig.hasLeadPageRedesignM3FF) {
            refetchForEvent('contactsUpdated');
        } else {
            $(window).trigger(Lead.events.LeadBackboneModelDidUpdate);
        }
    }, []);

    const handleCreateContact = (contact: ContactInput) => {
        const newContact: ContactCreateInput = {
            contact: {
                leads: [{id: props.leadId}],
            },
        };

        if (contact.name) {
            newContact.contact.name = contact.name;
        }

        if (props.contactParentAccount) {
            const accountData: EntityRelatedEntityInput = {id: props.contactParentAccount.id};
            if (contact.jobTitle) {
                accountData.relationship = contact.jobTitle;
            }
            newContact.contact.accounts = [accountData];
        }

        if (contact.phoneNumber) {
            newContact.contact.phones = [{number: contact.phoneNumber}];
        }

        if (contact.email) {
            newContact.contact.emailAddresses = [{address: contact.email}];
        }

        if (contact.description) {
            newContact.contact.description = contact.description;
        }
        props
            .onCreate(newContact)
            .then(() => {
                handleRefetchTrigger();
            })
            .catch((e) => {
                props.errorProps.setError(safelyGetGraphQLErrorMessage(e));
            });
        props.onClose();
    };

    // Called when the user selects an existing contact from the select
    const handleAddContact = (contact: DisplayOptionEntity) => {
        const newContact: ContactUpdateInput = {
            contact: {
                id: contact.id,
                leads: [{id: props.leadId}],
            },
        };
        if (props.contactParentAccount) {
            newContact.contact.accounts = [{id: props.contactParentAccount.id}];
        }
        props
            .onUpdate(newContact)
            .then(() => {
                handleRefetchTrigger();
            })
            .catch((e) => {
                props.errorProps.setError(safelyGetGraphQLErrorMessage(e));
            });
        props.onClose();
    };

    return (
        <ContactCardCreateable
            onSelectContact={handleAddContact}
            onSubmit={handleCreateContact}
            onClose={props.onClose}
            validate={(contactInput: ContactCreateValidationInput) =>
                fetchFormValidation(contactInput)
            }
            onSearchPotentialContacts={(searchInput: ContactInput) =>
                getPotentialContacts({
                    contactInput: searchInput,
                    relatedAccountIds: props.contactParentAccount
                        ? [props.contactParentAccount.id]
                        : props.alreadyAddedAccountIds,
                    alreadyAddedContactsIds: props.alreadyAddedContacts.map((c) => c.id),
                })
            }
            hasRelatedAccounts={Boolean(
                props.contactParentAccount || props.alreadyAddedAccountIds.length > 0
            )}
            showRoleField={Boolean(props.contactParentAccount?.id)}
            isPotentialContactsPopover={true}
        />
    );
}
